import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'

import { useAivyContext } from '../../../context'
import { isChallengeAvailable } from '../../../helpers/diagnostic'
import ChallengeCard from '../pieces/challenge-card'
import DividerWithText from './divider-with-text'

const I18N_PREFIX = 'create_career.adjust_test_battery.'

/**
 * Controlled. Der Draft liegt in Adjustment, damit es genau einen Writer auf
 * career_analyse gibt. Vorher hielt diese Komponente eine eigene
 * Formik-Instanz samt Speichern-Button, und der Footer las dieselben Daten
 * vom Server-Objekt.
 *
 * Dauer und Anzahl stehen im Seitenkopf, nicht hier: sie beschreiben die
 * ganze Konfiguration und sollen sichtbar bleiben, waehrend man durch das
 * Grid scrollt.
 */
const CustomizationTestBattery = ({
  configuration,
  availableExamIds,
  selectedExamIds,
  onToggleChallenge
}) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()

  // Ein Choke-Point: alles Abgeleitete (standardChallenges, moreChallenges,
  // getDuration, der Zaehler "X von Y") haengt an allChallenges. Die
  // Culture-Challenge des jeweils anderen Modus hier herauszufiltern reicht
  // deshalb aus (siehe isChallengeAvailable in helpers/diagnostic.js).
  const allChallenges = useMemo(
    () =>
      availableExamIds
        .filter((exam_id) => exam_id !== 'PATH_FINDER')
        .filter((exam_id) => isChallengeAvailable(configuration[exam_id])),
    [availableExamIds, configuration]
  )

  const toCard = (exam_id) => ({
    exam_id,
    ...system.challenges[exam_id],
    ...configuration[exam_id]
  })

  const standardChallenges = allChallenges
    .map(toCard)
    .filter(({ standard }) => standard)

  const moreChallenges = allChallenges
    .map(toCard)
    .filter(({ standard }) => !standard)

  return (
    <>
      <h2 className='text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight'>
        {t(I18N_PREFIX + 'title')}
      </h2>
      <ReactMarkdown className='mt-2 max-w-2xl text-sm/6 text-gray-900'>
        {t(I18N_PREFIX + 'description')}
      </ReactMarkdown>
      <DividerWithText
        className='mt-6'
        text={t(I18N_PREFIX + 'standard_divider_title')}
      />
      <div className='mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2'>
        {standardChallenges.map((challenge) => (
          // key ueber exam_id statt ueber den Index: bei einem Re-Render mit
          // veraenderter Liste wandert der Fokus sonst auf eine andere Karte.
          // Ohne onClick ist die Karte reine Anzeige, ein selected waere hier
          // tot. Pflicht-Challenges duerfen nicht als Kontrollkaestchen
          // erscheinen, die niemand umschalten kann.
          <ChallengeCard key={challenge.exam_id} challenge={challenge} />
        ))}
      </div>
      <DividerWithText
        className='mt-6'
        text={t(I18N_PREFIX + 'more_divider_title')}
      />
      <div className='mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2'>
        {moreChallenges.map((challenge) => (
          <ChallengeCard
            key={challenge.exam_id}
            challenge={challenge}
            onClick={() => onToggleChallenge(challenge.exam_id)}
            selected={selectedExamIds.includes(challenge.exam_id)}
          />
        ))}
      </div>
    </>
  )
}

CustomizationTestBattery.propTypes = {
  configuration: PropTypes.object.isRequired,
  availableExamIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedExamIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleChallenge: PropTypes.func.isRequired
}

export default CustomizationTestBattery
